<script setup>
import PinAction from './PinAction.vue';
// SiteUp: pílula do funil no header desktop (etapa + tracinhos + valor).
// Reaproveita a mesma fonte de dados do KanbanChip; muda só a apresentação.
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import KanbanAPI from 'dashboard/api/kanban';
import ChipPopover from 'dashboard/components-next/Conversation/ContextChips/ChipPopover.vue';
import Modal from 'dashboard/components/Modal.vue';
import {
  FUNNEL_DASH_TOTAL,
  formatOpportunityValue,
  funnelFilledDashes,
  stagesFromFunnel,
} from './funnelPillState';

const props = defineProps({
  conversation: { type: Object, default: () => ({}) },
  open: { type: Boolean, default: false },
  // Modo trilho (V3): so o ponto na cor da etapa, funil e valor no tooltip.
  rail: { type: Boolean, default: false },
  // Com excecao na tela o fixado encolhe: so o valor, etapa no title (mockup).
  // Sem valor no card, sobra a etapa: encolher para nada nao ajuda ninguem.
  compact: { type: Boolean, default: false },
  // Acao de fixar no topo dentro do popover (V3): so onde o cabecalho pede.
  pinnable: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'close']);

const KanbanItemDetailView = defineAsyncComponent(
  () =>
    import(
      'dashboard/routes/dashboard/kanban/components/KanbanItemDetailView.vue'
    )
);

const { t, locale } = useI18n();
const store = useStore();
const router = useRouter();

const item = ref(null);
const loading = ref(false);
const moving = ref(false);
const cardOpen = ref(false);

const conversationId = computed(
  () => props.conversation?.id || props.conversation?.display_id
);

const funnels = computed(
  () =>
    store.getters['funnel/getFunnels']?.filter(funnel => funnel.active) || []
);

const itemFunnel = computed(() => {
  if (item.value?.funnel) return item.value.funnel;
  if (!item.value?.funnel_id) return null;
  return (
    funnels.value.find(f => String(f.id) === String(item.value.funnel_id)) ||
    null
  );
});

const stages = computed(() => stagesFromFunnel(itemFunnel.value));
const currentStageKey = computed(() => item.value?.funnel_stage || '');
const currentStageIndex = computed(() =>
  Math.max(
    0,
    stages.value.findIndex(stage => stage.key === currentStageKey.value)
  )
);
const currentStage = computed(
  () => stages.value[currentStageIndex.value] || null
);

const show = computed(() => Boolean(item.value?.id));
// Cor da etapa para o ponto do trilho; roxo neutro quando a etapa nao define.
const stageColorHex = computed(() => currentStage.value?.color || '#b78af7');
const stageName = computed(
  () => currentStage.value?.name || currentStageKey.value || ''
);
const filledDashes = computed(() =>
  funnelFilledDashes(currentStageIndex.value, stages.value.length)
);
const cardValue = computed(() =>
  formatOpportunityValue(
    item.value?.item_details?.value ?? item.value?.value,
    locale.value || 'pt-BR'
  )
);

const extractItems = payload => {
  if (Array.isArray(payload)) return payload;
  return payload?.items || payload?.payload || payload?.kanban_items || [];
};

const ensureFunnels = async () => {
  if (funnels.value.length) return;
  try {
    await store.dispatch('funnel/fetch');
  } catch {
    // keep empty
  }
};

const fetchItem = async () => {
  if (!conversationId.value) {
    item.value = null;
    return;
  }
  const cached = props.conversation?.kanban_items;
  if (Array.isArray(cached) && cached.length) {
    item.value = cached[0];
  }
  loading.value = true;
  try {
    const { data } = await KanbanAPI.getItemsByConversation(
      conversationId.value
    );
    const list = extractItems(data);
    item.value = list.length ? list[0] : null;
  } catch {
    if (!item.value) item.value = null;
  } finally {
    loading.value = false;
  }
};

const moveStage = async stageKey => {
  if (!item.value?.id || !stageKey || stageKey === item.value.funnel_stage) {
    return;
  }
  moving.value = true;
  try {
    const updated = await KanbanAPI.moveToStage(
      item.value.id,
      stageKey,
      item.value.funnel_id
    );
    if (updated) item.value = { ...item.value, ...updated };
    else item.value = { ...item.value, funnel_stage: stageKey };
  } catch {
    // stay
  } finally {
    moving.value = false;
  }
};

const openCard = () => {
  if (!item.value?.id) return;
  cardOpen.value = true;
  emit('close');
};

const openKanban = () => {
  if (!item.value?.id) return;
  router.push({
    name: 'kanban_board',
    query: { item: String(item.value.id) },
  });
  emit('close');
};

onMounted(ensureFunnels);

watch(
  () => conversationId.value,
  () => fetchItem(),
  { immediate: true }
);

watch(
  () => props.conversation?.kanban_items?.[0],
  updated => {
    if (!updated || String(updated.id) !== String(item.value?.id)) return;
    item.value = { ...item.value, ...updated };
  }
);

watch(
  () => props.open,
  open => {
    if (open) {
      fetchItem();
      ensureFunnels();
    }
  }
);
</script>

<template>
  <div v-if="show" class="inline-flex" data-testid="header-funnel-pill">
    <ChipPopover :open="open" width-class="w-72" @close="emit('close')">
      <template #trigger>
        <button
          v-if="rail"
          type="button"
          class="flex size-9 items-center justify-center rounded-xl hover:bg-n-alpha-2"
          :class="{ 'bg-n-alpha-2': open }"
          :aria-label="stageName"
          :title="stageName"
          @click="emit('toggle')"
        >
          <span
            class="size-3 rounded-full ring-2 ring-n-alpha-2"
            :style="{ backgroundColor: stageColorHex }"
            aria-hidden="true"
          />
        </button>
        <button
          v-else
          type="button"
          class="inline-flex min-h-8 w-fit max-w-[240px] flex-col items-start gap-1 rounded-lg border border-transparent bg-transparent px-2 py-1 text-left hover:bg-n-alpha-2"
          :class="{ 'bg-n-solid-2': open }"
          :aria-label="stageName"
          :title="stageName"
          @click="emit('toggle')"
        >
          <span
            class="min-w-0 whitespace-normal break-words text-[12.5px] font-bold leading-snug text-n-slate-12"
          >
            {{ stageName }}
          </span>
          <span
            v-if="!compact || cardValue"
            class="inline-flex items-center gap-2"
          >
            <span
              v-if="!compact"
              class="inline-flex shrink-0 items-center gap-0.5"
              aria-hidden="true"
            >
              <span
                v-for="dash in FUNNEL_DASH_TOTAL"
                :key="dash"
                class="h-0.5 w-2 rounded-full"
                :class="dash <= filledDashes ? 'bg-n-teal-9' : 'bg-n-solid-3'"
              />
            </span>
            <span
              v-if="cardValue"
              class="shrink-0 text-[12px] font-bold tabular-nums text-n-slate-11"
            >
              {{ cardValue }}
            </span>
          </span>
        </button>
      </template>

      <p
        class="whitespace-normal break-words text-[14px] font-medium text-n-slate-12"
      >
        {{ stageName }}
      </p>
      <p v-if="cardValue" class="mt-1 text-[13px] font-bold text-n-slate-12">
        {{ cardValue }}
      </p>
      <label class="mt-3 block text-[12px] font-bold text-n-slate-11">
        {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.KANBAN.STAGE') }}
        <!-- SiteUp: este popover e teleportado para o body, logo fica fora do
             escopo [dir="ltr"] que posiciona a seta global do select a direita.
             Sem isso a seta cai no canto superior esquerdo, por cima do texto.
             Chevron proprio nao depende do dir herdado. -->
        <span class="relative mt-1 block">
          <select
            class="h-9 w-full appearance-none rounded-full border border-n-weak bg-n-solid-2 bg-none pl-3 pr-9 text-[13px] font-bold text-n-slate-12"
            :value="currentStageKey"
            :disabled="moving || loading"
            @change="moveStage($event.target.value)"
          >
            <option v-for="stage in stages" :key="stage.key" :value="stage.key">
              {{ stage.name }}
            </option>
          </select>
          <span
            class="i-ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-11"
            aria-hidden="true"
          />
        </span>
      </label>
      <button
        type="button"
        class="mt-3 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-full border border-n-weak px-3 text-[13px] font-bold text-n-slate-12 hover:bg-n-alpha-2"
        @click="openCard"
      >
        <span class="i-ri-article-line size-4 shrink-0" aria-hidden="true" />
        {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.KANBAN.OPEN_CARD') }}
      </button>
      <button
        type="button"
        class="mt-2 inline-flex h-7 w-full items-center justify-center gap-1 rounded-full px-3 text-[12px] font-bold text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
        @click="openKanban"
      >
        <span
          class="i-ri-external-link-line size-3.5 shrink-0"
          aria-hidden="true"
        />
        {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.KANBAN.OPEN') }}
      </button>
      <template v-if="pinnable" #actions>
        <PinAction modulo="funnel" @done="emit('close')" />
      </template>
    </ChipPopover>
  </div>

  <Modal
    v-model:show="cardOpen"
    size="full-width"
    :on-close="() => (cardOpen = false)"
  >
    <div class="max-h-[88dvh] w-full overflow-y-auto bg-n-solid-1">
      <KanbanItemDetailView
        v-if="cardOpen && item?.id"
        :item-id="item.id"
        :sync-query="false"
        @back="cardOpen = false"
        @item-updated="fetchItem"
      />
    </div>
  </Modal>
</template>
